import { useEffect, useState } from 'react';
import { Grid, TextField, MenuItem, Alert, CircularProgress, Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import {
  getAutojobById,
  getAutojob2ById,
  updateAutojob,
  updateAutojob2,
  deleteAutojob,
  deleteAutojob2,
  UpdateAutojobRequest,
} from 'src/shared/services/autojobService';
import { AutojobResponse } from 'src/modules/dashboard/models/autojobResponse';
import { formatDateAndTime } from 'src/shared/functions/formatDate';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';

type EditAutojobModalProps = {
  taskId: number;
  version: 1 | 2;
  onClose: () => void;
  onSaved: () => void;
};

const scheduleTypeOptions = [
  { value: 0, labelKey: 'settings:autojobs.scheduleTypes.once' },
  { value: 1, labelKey: 'settings:autojobs.scheduleTypes.minute' },
  { value: 2, labelKey: 'settings:autojobs.scheduleTypes.hourly' },
  { value: 3, labelKey: 'settings:autojobs.scheduleTypes.daily' },
  { value: 4, labelKey: 'settings:autojobs.scheduleTypes.weekly' },
  { value: 5, labelKey: 'settings:autojobs.scheduleTypes.monthly' },
  { value: 6, labelKey: 'settings:autojobs.scheduleTypes.yearly' },
];

// Samma statusvärden som flikarna på autojobbsidan filtrerar på.
const statusOptions = [
  { value: 1, labelKey: 'settings:autojobs.statusTabs.ready' },
  { value: 2, labelKey: 'settings:autojobs.statusTabs.running' },
  { value: 3, labelKey: 'settings:autojobs.statusTabs.onHold' },
  { value: 10, labelKey: 'settings:autojobs.statusTabs.completed' },
  { value: 15, labelKey: 'settings:autojobs.statusTabs.error' },
];

const toForm = (job: AutojobResponse): UpdateAutojobRequest => ({
  task_id: job.task_id,
  // taskName: job.taskName ?? '',
  // assemblyName: job.assemblyName ?? '',
  scheduleType: job.scheduleType,
  increaseNum: job.increaseNum,
  action: job.action,
  status: job.status,
  startTimeStamp: job.startTimeStamp ? dayjs(job.startTimeStamp).format('YYYY-MM-DDTHH:mm') : '',
  successEmail: job.successEmail ?? '',
  failEmail: job.failEmail ?? '',
  custom1: job.custom1 ?? '',
  custom2: job.custom2 ?? '',
  custom3: job.custom3 ?? '',
  custom4: job.custom4 ?? '',
  custom5: job.custom5 ?? '',
  custom6: job.custom6 ?? '',
  custom7: job.custom7 ?? '',
  custom8: job.custom8 ?? '',
  custom9: job.custom9 ?? '',
  custom10: job.custom10 ?? '',
  custom11: job.custom11 ?? '',
  custom12: job.custom12 ?? '',
});

export const EditAutojobModal = (props: EditAutojobModalProps) => {
  const { taskId, version, onClose, onSaved } = props;
  const { t } = useTranslation(['settings', 'dashboard', 'common']);
  const isMounted = useIsMounted();
  const [job, setJob] = useState<AutojobResponse | null>(null);
  const [form, setForm] = useState<UpdateAutojobRequest | null>(null);
  const [saving, setSaving] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  // startTimeStamp redigeras som separat datum och tid och slås ihop vid sparning.
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const result = version === 1 ? await getAutojobById(taskId) : await getAutojob2ById(taskId);
        if (isMounted()) {
          setJob(result.data);
          setForm(toForm(result.data));
          const start = result.data.startTimeStamp ? dayjs(result.data.startTimeStamp) : null;
          setStartDate(start);
          setStartTime(start);
        }
      } catch (e) {
        ConsoleHelper.log(e);
        toast.error(<>{t('common:statusMessages.generalError')}</>);
        onClose();
      }
    };
    fetchJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, version]);

  const updateField = (field: keyof UpdateAutojobRequest, value: string | number) => {
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSave = async () => {
    if (!form || saving) return;
    // Antingen båda tomma (ingen starttid) eller båda giltiga.
    if (!!startDate !== !!startTime || (startDate && !startDate.isValid()) || (startTime && !startTime.isValid())) return;
    const startTimeStamp = startDate && startTime ? startDate.hour(startTime.hour()).minute(startTime.minute()).format('YYYY-MM-DDTHH:mm') : '';
    setSaving(true);
    try {
      const request = { ...form, startTimeStamp };
      await (version === 1 ? updateAutojob(request) : updateAutojob2(request));
      toast.success(<>{t('common:statusMessages.saveChangesSuccess')}</>);
      onSaved();
    } catch {
      toast.error(<>{t('common:statusMessages.saveChangesError')}</>);
    } finally {
      if (isMounted()) {
        setSaving(false);
      }
    }
  };

  const handleDelete = async () => {
    setOpenDeleteDialog(false);
    if (saving) return;
    setSaving(true);
    try {
      await (version === 1 ? deleteAutojob(taskId) : deleteAutojob2(taskId));
      toast.success(<>{t('common:success.delete')}</>);
      onSaved();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(<>{t('common:error.delete')}</>);
    } finally {
      if (isMounted()) {
        setSaving(false);
      }
    }
  };

  const customFields = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <>
      <GenericDialog
        open
        dialogTitle={job?.taskName || t('settings:autojobs.editModal.title')}
        maxWidth='sm'
        onClose={onClose}
        onSave={handleSave}
        dialogActions={
          <Grid container justifyContent='space-between'>
            <Grid item>
              <Button variant='outlined' color='error' disableElevation disabled={!job || saving} onClick={() => setOpenDeleteDialog(true)}>
                {t('common:button.delete')}
              </Button>
            </Grid>
            <Grid item>
              <Button variant='outlined' color='primary' disableElevation onClick={onClose}>
                {t('common:button.cancel')}
              </Button>
              <Button sx={{ ml: 1 }} variant='contained' color='primary' disableElevation disabled={!form || saving} onClick={handleSave}>
                {t('common:button.save')}
              </Button>
            </Grid>
          </Grid>
        }
        dialogContent={
          !job || !form ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography>Data</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField label='ID' value={job.task_id} fullWidth size='small' disabled />
              </Grid>
              <Grid item xs={12}>
                <TextField label={t('settings:autojobs.name')} value={job.taskName ?? ''} fullWidth size='small' disabled />
              </Grid>
              <Grid item xs={12}>
                <TextField label='Assembly' value={job.assemblyName ?? ''} fullWidth size='small' disabled />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('settings:autojobs.editModal.status')}
                  value={form.status}
                  onChange={(e) => updateField('status', Number(e.target.value))}
                  fullWidth
                  size='small'
                  select
                  disabled={saving}
                >
                  {/* Visa även okända statusvärden så att fältet inte blir tomt */}
                  {!statusOptions.some((opt) => opt.value === form.status) && <MenuItem value={form.status}>{form.status}</MenuItem>}
                  {statusOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label={t('settings:autojobs.selectionModal.startDate')}
                  value={startDate}
                  onChange={(val) => setStartDate(val)}
                  format='YYYY-MM-DD'
                  disabled={saving}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePicker
                  label={t('settings:autojobs.selectionModal.startTime')}
                  value={startTime}
                  onChange={(val) => setStartTime(val)}
                  ampm={false}
                  disabled={saving}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('settings:autojobs.scheduleType')}
                  value={form.scheduleType}
                  onChange={(e) => updateField('scheduleType', Number(e.target.value))}
                  fullWidth
                  size='small'
                  select
                  disabled={saving}
                >
                  {scheduleTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('dashboard:autojobs.interval')}
                  value={form.increaseNum}
                  onChange={(e) => updateField('increaseNum', Number(e.target.value))}
                  fullWidth
                  size='small'
                  type='number'
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('settings:autojobs.editModal.successEmail')}
                  value={form.successEmail}
                  onChange={(e) => updateField('successEmail', e.target.value)}
                  fullWidth
                  size='small'
                  type='email'
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('settings:autojobs.editModal.failEmail')}
                  value={form.failEmail}
                  onChange={(e) => updateField('failEmail', e.target.value)}
                  fullWidth
                  size='small'
                  type='email'
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('settings:autojobs.editModal.created')}
                  value={formatDateAndTime(job.createdTimeStamp)}
                  fullWidth
                  size='small'
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('dashboard:autojobs.latestExecution')}
                  value={formatDateAndTime(job.lastExecuted)}
                  fullWidth
                  size='small'
                  disabled
                />
              </Grid>
              {(job.statusMsg || job.lastStatusMsg) && (
                <Grid item xs={12}>
                  <TextField
                    label={t('settings:autojobs.message')}
                    value={job.statusMsg || job.lastStatusMsg}
                    fullWidth
                    size='small'
                    multiline
                    disabled
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography>Custom</Typography>
              </Grid>
              {/*
              <Grid item xs={12}>
                <Alert severity='info'>{t('settings:autojobs.editModal.readOnlyInfo')}</Alert>
              </Grid>
              */}
              {customFields.map((num) => {
                const key = `custom${num}` as keyof UpdateAutojobRequest;
                return (
                  <Grid item xs={6} key={key}>
                    <TextField
                      label={`Custom ${num}`}
                      value={form[key] ?? ''}
                      onChange={(e) => updateField(key, e.target.value)}
                      fullWidth
                      size='small'
                      disabled={saving}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )
        }
      />
      <DeleteConfirmationDialog
        title={t('common:confirmDelete')}
        value=''
        modaltext={t('common:irreversibleAction')}
        onClose={(value) => {
          if (value) {
            handleDelete();
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
      />
    </>
  );
};
